import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, Mail, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { toast } from "sonner";

const linkCols = [
    {
        title: "Shop",
        links: [
            { label: "Nuovi Arrivi", to: "/collection/nuovi-arrivi" },
            { label: "Completi", to: "/collection/completi" },
            { label: "Calzature", to: "/collection/calzature" },
            { label: "Accessori", to: "/collection/accessori" },
            { label: "Outlet", to: "/collection/outlet" },
        ],
    },
    {
        title: "Aiuto",
        links: [
            { label: "Contattaci", to: "/contatti" },
            { label: "FAQ", to: "/faq" },
            { label: "Spedizioni", to: "/spedizioni" },
            { label: "Resi & Rimborsi", to: "/resi" },
            { label: "Guida alle Taglie", to: "/taglie" },
        ],
    },
    {
        title: "Mini Style",
        links: [
            { label: "Chi Siamo", to: "/chi-siamo" },
            { label: "Il Blog", to: "/blog" },
            { label: "Privacy Policy", to: "/privacy" },
            { label: "Termini e Condizioni", to: "/termini" },
        ],
    },
];

export default function Footer() {
    const { homepage } = useApp();
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const r = await api.post("/newsletter/subscribe", { email });
            toast.success(`Benvenuto/a in Mini Style! Usa il codice ${r.data.discount_code || "WELCOME10"} per il 10% di sconto sul primo ordine.`);
            setEmail("");
        } catch (err) {
            toast.error("Iscrizione non riuscita. Riprova.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer className="bg-[color:var(--ms-cream)] border-t border-[color:var(--ms-border)] mt-24" data-testid="site-footer">
            {/* Newsletter band */}
            <div className="px-6 md:px-12 lg:px-24 py-20 border-b border-[color:var(--ms-border)] grid md:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="overline mb-3">Newsletter</div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[color:var(--ms-charcoal)]">
                        {homepage?.newsletter_headline || "Entra nel mondo Mini Style"}
                    </h2>
                    <p className="text-[color:var(--ms-muted)] mt-4 max-w-md text-sm">
                        {homepage?.newsletter_offer || "Scopri nuovi arrivi, collezioni e occasioni speciali. -10% sul primo ordine."}
                    </p>
                </div>
                <form onSubmit={subscribe} className="flex items-center gap-0 border-b-2 border-[color:var(--ms-charcoal)] w-full" data-testid="newsletter-form">
                    <Mail size={16} className="text-[color:var(--ms-sage-dark)]" />
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="La tua email"
                        className="bg-transparent border-none outline-none flex-1 px-4 py-3 text-[color:var(--ms-charcoal)] placeholder:text-[color:var(--ms-muted)] text-sm"
                        data-testid="newsletter-email-input"
                    />
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-6 py-3 text-xs tracking-[0.2em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-colors disabled:opacity-50"
                        data-testid="newsletter-submit"
                    >
                        {submitting ? "…" : "ISCRIVITI"}
                    </button>
                </form>
            </div>

            {/* Link columns */}
            <div className="px-6 md:px-12 lg:px-24 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
                <div className="col-span-2">
                    {homepage?.logo_image_url ? (
                        <img src={homepage.logo_image_url} alt="Mini Style" className="h-28 w-auto object-contain -ml-2 mb-4" style={{ mixBlendMode: "multiply" }} />
                    ) : (
                        <>
                            <div className="font-display font-italic-display text-3xl text-[color:var(--ms-charcoal)] mb-2">{homepage?.logo_text || "Mini Style"}</div>
                            <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ms-brown)] mb-5 font-semibold">{homepage?.logo_tagline || "abbigliamento e calzature da 0/16 anni"}</div>
                        </>
                    )}
                    <p className="text-[color:var(--ms-muted)] text-sm leading-relaxed max-w-sm">
                        {homepage?.footer_about}
                    </p>
                    <div className="flex gap-4 mt-6 text-[color:var(--ms-charcoal)]">
                        {homepage?.footer_instagram && (
                            <a href={homepage.footer_instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[color:var(--ms-sage-dark)]" data-testid="footer-instagram"><Instagram size={18} /></a>
                        )}
                        {homepage?.footer_tiktok && (
                            <a href={homepage.footer_tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:text-[color:var(--ms-sage-dark)]" data-testid="footer-tiktok"><Music2 size={18} /></a>
                        )}
                    </div>
                    {homepage?.footer_email && (
                        <a href={`mailto:${homepage.footer_email}`} className="mt-5 flex items-center gap-2 text-sm text-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-sage-dark)] link-underline w-fit">
                            <Mail size={14} /> {homepage.footer_email}
                        </a>
                    )}
                </div>
                {linkCols.map(col => (
                    <div key={col.title}>
                        <div className="overline mb-4">{col.title}</div>
                        <ul className="space-y-2.5">
                            {col.links.map(l => (
                                <li key={l.label}>
                                    <Link to={l.to} className="text-sm text-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-sage-dark)] transition-colors link-underline" data-testid={`footer-link-${l.label.toLowerCase().replace(/ /g, '-')}`}>{l.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="px-6 md:px-12 lg:px-24 py-6 border-t border-[color:var(--ms-border)] flex flex-col md:flex-row justify-between text-xs text-[color:var(--ms-muted)] tracking-wide gap-2">
                <span>&copy; {new Date().getFullYear()} Mini Style. Tutti i diritti riservati.</span>
                <span className="flex items-center gap-1.5"><MapPin size={12} /> Boutique italiana · Spedizioni in tutta Italia</span>
            </div>
        </footer>
    );
}
